import ledgerRaw from '@/data/journalLedger.txt?raw';

export interface JournalEntry {
  instrument: string;
  side: 'BUY' | 'SELL';
  date: string;
  entry: string;
  exit: string;
  volume: string;
  result: string;
  profit: boolean;
}

export interface TradeRecord {
  id: number;
  date: string;
  side: 'BUY' | 'SELL';
  entry: number;
  exit: number;
  volume: number;
  profit: number;
}

// The raw ledger only ever holds prices — never a hand-typed result.
// The profit/loss is derived from entry/exit prices below, so the result can
// never drift out of sync with the prices it is supposed to reflect.
interface RawTrade {
  id: number;
  date: string;
  side: 'BUY' | 'SELL';
  entry: number;
  exit: number;
  volume: number;
}

// XAUUSD contract value: a 1.00 price move on a 1.00 lot position = $100.
// Change this single constant if the instrument or account contract size differs.
const POINT_VALUE = 100;

// Fallback opening balance, used only if the document does not declare one.
const DEFAULT_STARTING_BALANCE = 6380;

// Profit is computed straight from the prices:
//   BUY  → (exit − entry) × volume × point value
//   SELL → (entry − exit) × volume × point value
// To update the record you now only ever fill in prices; the result follows.
export const computeProfit = (trade: {
  side: 'BUY' | 'SELL';
  entry: number;
  exit: number;
  volume: number;
}): number => {
  const move = trade.side === 'BUY' ? trade.exit - trade.entry : trade.entry - trade.exit;
  return Number((move * trade.volume * POINT_VALUE).toFixed(2));
};

// ───────────────────────────────────────────────────────────────────────────
//  DATA SOURCE
//  The trades are NOT stored in this file. They live in a standalone plain-text
//  document:  src/data/journalLedger.txt
//  Download that document, edit it, and upload it back to update the site —
//  no code changes are ever needed here. The format is described at the top of
//  that file (one trade per line: date | side | entry | exit | volume).
// ───────────────────────────────────────────────────────────────────────────

// Turns the plain-text ledger into the structured record list the rest of the
// site consumes. Blank lines, "#" comment lines and "@" setting lines are skipped.
const parseLedger = (block: string): RawTrade[] =>
  block
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#') && !line.startsWith('@'))
    .map((line, index) => {
      const [date, side, entry, exit, volume] = line.split('|').map((part) => part.trim());
      return {
        id: index + 1,
        date,
        side: side === 'SELL' ? 'SELL' : 'BUY',
        entry: Number(entry),
        exit: Number(exit),
        volume: Number(volume),
      };
    });

const rawTrades: RawTrade[] = parseLedger(ledgerRaw);

// The opening balance is declared in the same document (journalLedger.txt) as
// the setting line `@STARTING_BALANCE = 6380`, so it is edited right alongside
// the trades instead of being buried in code. Every balance and the account
// curve are built on top of this value.
const parseStartingBalance = (block: string): number => {
  const match = block.match(/^\s*@STARTING_BALANCE\s*=\s*([0-9.,]+)/im);
  if (!match) return DEFAULT_STARTING_BALANCE;
  const value = Number(match[1].replace(/,/g, ''));
  return Number.isFinite(value) ? value : DEFAULT_STARTING_BALANCE;
};

export const STARTING_BALANCE = parseStartingBalance(ledgerRaw);

// The public ledger: same shape as before, but `profit` is now derived so it can
// never disagree with the prices. Nothing downstream needs to change.
export const tradeRecords: TradeRecord[] = rawTrades.map((trade) => ({
  ...trade,
  profit: computeProfit(trade),
}));

export const systemFlow = [
  { key: 'MARKET', detail: 'Monitor live XAUUSD price action and volatility.' },
  { key: 'ANALYZE', detail: 'Evaluate trend momentum and key structural levels.' },
  { key: 'FILTER', detail: 'Apply strict risk parameters and news filters.' },
  { key: 'DECIDE', detail: 'Confirm high-probability setups via core algorithm.' },
  { key: 'EXECUTE', detail: 'Trigger precision entries automatically via MT5.' },
];

export const systemPath = [
  {
    index: '01',
    title: 'Monitor',
    detail:
      'Scan XAUUSD price action, liquidity and volatility continuously across multiple timeframes, tracking structural highs, lows and shifts as they form.',
  },
  {
    index: '02',
    title: 'Evaluate',
    detail:
      'Test each candidate setup against the system’s predefined conditions — trend alignment, momentum and the key structural levels that define the current market context.',
  },
  {
    index: '03',
    title: 'Decide',
    detail:
      'Confirm whether the setup qualifies as a valid, risk-compliant entry. If even one condition is missing, the signal is rejected and no position is opened.',
  },
  {
    index: '04',
    title: 'Execute',
    detail:
      'Route a confirmed setup to MT5 and open the position at the calculated entry price, with predefined volume, stop loss and take profit attached automatically.',
  },
  {
    index: '05',
    title: 'Manage',
    detail:
      'Track the open trade through to its exit — profit targets, protective stops and time-based conditions are applied automatically, with no manual intervention required.',
  },
];

export const mt5Steps = [
  { index: '01', title: 'Install', detail: 'Install the EA in your MT5 environment.' },
  { index: '02', title: 'Configure', detail: 'Apply the required settings.' },
  { index: '03', title: 'Run', detail: 'Enable automated trading and monitor the system.' },
];